<template>
  <v-card class="ship mx-auto" :loading="loading" max-width="600">
    <template v-if="!loading">
      <v-img
        class="white--text align-end"
        :src="ship.image_url"
        :alt="ship.name"
      ></v-img>
      <v-card-title>{{ ship.name }}</v-card-title>
      <v-card-subtitle>{{ ship.group }}</v-card-subtitle>
      <v-tabs v-model="tab" show-arrows center-active>
        <v-tab v-for="item in items" :key="item">
          {{ item }}
        </v-tab>

        <v-tab-item>
          <v-card-text>
            <p
              v-for="(para, index) in ship.description.split('\r\n')"
              :key="index"
              v-html="para"
            />
          </v-card-text>
        </v-tab-item>
        <v-tab-item>
          <v-card-text>
            <div
              class="sub-section"
              v-for="(attributes, key) in ship.attributes"
              :key="key"
            >
              <h2>{{ key | underscore_to_space | capitalize }}</h2>
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                    <tr v-for="item in attributes" :key="item.name">
                      <td>{{ item.display_name }}</td>
                      <td class="text-right">{{ item.value | add_commas }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-card-text>
        </v-tab-item>
        <v-tab-item>
          <v-card-text>
            <v-simple-table>
              <template v-slot:default>
                <tbody>
                  <tr v-for="item in ship.fitting" :key="item.name">
                    <td>{{ item.display_name }}</td>
                    <td class="text-right">{{ item.value | add_commas }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-tab-item>
        <v-tab-item>
          <v-card-text>
            <v-simple-table>
              <template v-slot:default>
                <tbody>
                  <tr
                    v-for="(item, index) in ship.requirements"
                    :key="`req${index}`"
                  >
                    <td :class="`indent--${item.depth}`">
                      {{ item.name }}
                    </td>
                    <td class="text-right">
                      <span
                        class="box"
                        v-for="n in item.value"
                        :key="`box-${n}`"
                      ></span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-tab-item>
      </v-tabs>
    </template>
  </v-card>
</template>

<script>
import { mapState } from "vuex";

const required_skill_attribute_ids = [182, 183, 184, 1285, 1289, 1290];
const required_level_attribute_ids = [277, 278, 279, 1286, 1287, 1288];
const roman_numerals = ["", "I", "II", "III", "IV", "V"];
function getRequirements(
  dogma,
  parent_attributes,
  all_skills,
  level = 0,
  requirements = []
) {
  const skill_level = parent_attributes.filter(y => {
    const index = required_skill_attribute_ids.indexOf(dogma.attribute_id);
    return y.attribute_id == required_level_attribute_ids[index];
  })[0].value;
  const active_skill = all_skills.find(x => x.type_id == dogma.value);
  const skill = {
    name: `${active_skill.name} ${roman_numerals[skill_level]}`,
    value: skill_level,
    depth: level
  };
  requirements.push(skill);
  const requirement_attributes = (active_skill.dogma_attributes || [])
    .filter(x => required_skill_attribute_ids.includes(x.attribute_id))
    .sort(
      (a, b) =>
        required_skill_attribute_ids.indexOf(a.attribute_id) -
        required_skill_attribute_ids.indexOf(b.attribute_id)
    );
  if (requirement_attributes.length > 0) {
    const new_level = level + 1;
    for (let nested_dogma of requirement_attributes) {
      getRequirements(
        nested_dogma,
        active_skill.dogma_attributes,
        all_skills,
        new_level,
        requirements
      );
    }
  }
  return requirements;
}

function gatherAttributes(fields, attributes) {
  return attributes
    .filter(x => fields.map(y => y[0]).includes(x.name))
    .sort(
      (a, b) =>
        fields.map(y => y[0]).indexOf(a.name) -
        fields.map(y => y[0]).indexOf(b.name)
    )
    .map(x => {
      const unit = fields.filter(y => y[0] == x.name)[0][1];
      if (Array.isArray(unit)) {
        x.value = unit.find(y => y.type_id == x.value).name;
        return x;
      }
      switch (unit) {
        case "":
          return x;
        case "resonance%":
          x.value = Math.round(100 - x.value * 100) + "%";
          break;
        case "exact%":
          x.value = Math.round(x.value * 100) + "%";
          break;
        default:
          x.value = `${x.value} ${unit}`;
          break;
      }
      return x;
    });
}

export default {
  name: "ShipCard",
  props: {
    ship_id: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      base_url: "https://esi.evetech.net/latest",
      items: ["Description", "Attributes", "Fitting", "Requirements"],
      tab: "Description",
      ship: null
    };
  },
  watch: {
    ship_id() {
      this.loadShipDetails();
    }
  },
  computed: {
    ...mapState({
      skills: "skills",
      base_ship(state) {
        return state.ships.find(x => x.type_id == this.ship_id);
      },
      ship_groups: "ship_groups",
      dogma_attributes: "dogma_attributes"
    })
  },
  filters: {
    underscore_to_space(value) {
      return value.replace(/_/g, " ");
    },
    capitalize(value) {
      if (!value) return "";
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
  methods: {
    loadShipDetails() {
      const ship_dogma_attributes = this.base_ship.dogma_attributes
        .map(x => x.attribute_id);
      const type_ids = [];
      if (ship_dogma_attributes.includes(866)) {
        const jump_fuel_type_id = this.base_ship.dogma_attributes
          .find(x => x.attribute_id == 866)
          .value;
        type_ids.push(jump_fuel_type_id);
      }
      Promise.all([
        this.base_ship,
        this.ship_groups.find(x => x.group_id == this.base_ship.group_id),
        this.$store.dispatch("loadDogmaAttributes", ship_dogma_attributes),
        this.$store.dispatch("loadTypes", type_ids)
      ]).then(([ship, group, attributes, types]) => {
        const dogma_attributes = ship.dogma_attributes.map(x => {
          const attribute = attributes.filter(
            y => y.attribute_id == x.attribute_id
          )[0];
          return {
            ...attribute,
            value: x.value
          };
        });
        const ship_object = {
          type: this.ship_id,
          name: ship.name,
          description: ship.description,
          image_url: `https://images.evetech.net/types/${this.ship_id}/render`,
          linked_dogma: dogma_attributes,
          group: group.name,
          attributes: {}
        };

        const structure_fields = [
          ["hp", "HP"],
          ["capacity", "m3"],
          ["droneCapacity", "m3"],
          ["droneBandwidth", "Mbit/sec"],
          ["mass", "kg"],
          ["volume", "m3"],
          ["agility", "x"],
          ["specialOreHoldCapacity", ""],
          ["specialCorpseHoldCapacity", "m3"],
          ["specialCommandCenterHoldCapacity", "m3"],
          ["specialPlanetaryCommoditiesHoldCapacity", "m3"],
          ["specialMineralHoldCapacity", "m3"],
          ["specialAmmoHoldCapacity", "m3"],
          ["emDamageResonance", "resonance%"],
          ["thermalDamageResonance", "resonance%"],
          ["kineticDamageResonance", "resonance%"],
          ["explosiveDamageResonance", "resonance%"]
        ];
        const structure_attributes = gatherAttributes(
          structure_fields,
          dogma_attributes
        );
        ship_object.attributes.structure = structure_attributes;

        const armor_fields = [
          ["armorHP", "HP"],
          ["armorEmDamageResonance", "resonance%"],
          ["armorThermalDamageResonance", "resonance%"],
          ["armorKineticDamageResonance", "resonance%"],
          ["armorExplosiveDamageResonance", "resonance%"]
        ];
        const armor_attributes = gatherAttributes(
          armor_fields,
          dogma_attributes
        );
        ship_object.attributes.armor = armor_attributes;

        const shield_fields = [
          ["shieldCapacity", "HP"],
          ["shieldRechargeRate", "s"],
          ["shieldEmDamageResonance", "resonance%"],
          ["shieldThermalDamageResonance", "resonance%"],
          ["shieldKineticDamageResonance", "resonance%"],
          ["shieldExplosiveDamageResonance", "resonance%"]
        ];
        const shield_attributes = gatherAttributes(
          shield_fields,
          dogma_attributes
        );
        ship_object.attributes.shield = shield_attributes;

        const resistance_fields = [
          ["remoteAssistanceImpedance", "resonance%"],
          ["remoteRepairImpedance", "resonance%"],
          ["energyWarfareResistance", "resonance%"],
          ["sensorDampenerResistance", "resonance%"],
          ["stasisWebifierResistance", "resonance%"],
          ["targetPainterResistance", "resonance%"],
          ["weaponDisruptionResistance", "resonance%"]
        ];
        const resistance_attributes = gatherAttributes(
          resistance_fields,
          dogma_attributes
        );
        if (resistance_attributes.length > 0) {
          ship_object.attributes.electronic_resistances = resistance_attributes;
        }

        const capacitor_fields = [
          ["capacitorCapacity", "GJ"],
          ["rechargeRate", "s"]
        ];
        const capacitor_attributes = gatherAttributes(
          capacitor_fields,
          dogma_attributes
        );
        ship_object.attributes.capacitor = capacitor_attributes;

        const targeting_fields = [
          ["maxTargetRange", "km"],
          ["maxLockedTargets", ""],
          ["signatureRadius", "m"],
          ["scanResolution", "mm"],
          ["scanRadarStrength", "points"],
          ["scanMagnetometricStrength", "points"],
          ["scanGravimetricStrength", "points"],
          ["scanLadarStrength", "points"]
        ];
        const targeting_attributes = gatherAttributes(
          targeting_fields,
          dogma_attributes
        );
        ship_object.attributes.targeting = targeting_attributes;

        const shared_fields = [
          ["fleetHangarCapacity", "m3"],
          ["shipMaintenanceBayCapacity", "m3"],
          ["maxJumpClones", ""]
        ];
        const shared_attributes = gatherAttributes(
          shared_fields,
          dogma_attributes
        );
        if (shared_attributes.length > 0) {
          ship_object.attributes.shared_facilities = shared_attributes;
        }

        const fighter_fields = [
          ["fighterCapacity", "m3"],
          ["fighterTubes", ""],
          ["fighterLightSlots", ""],
          ["fighterSupportSlots", ""],
          ["fighterHeavySlots", ""]
        ];
        const fighter_attributes = gatherAttributes(
          fighter_fields,
          dogma_attributes
        );
        if (fighter_attributes.length > 0) {
          ship_object.attributes.fighter_squadron_facilities = fighter_attributes;
        }

        const jump_fields = [
          ["jumpDriveCapacitorNeed", "exact%"],
          ["jumpDriveRange", "ly"],
          ["jumpDriveConsumptionType", types],
          ["jumpDriveConsumptionAmount", "units"],
          ["jumpPortalCapacitorNeed", "GJ"],
          ["specialFuelBayCapacity", "m3"]
        ];
        const jump_attributes = gatherAttributes(jump_fields, dogma_attributes);
        if (jump_attributes.length > 0) {
          ship_object.attributes.jump_drive_systems = jump_attributes;
        }

        const propulsion_fields = [
          ["maxVelocity", "m/sec"],
          ["warpSpeedMultiplier", "AU/s"]
        ];
        const propulsion_attributes = gatherAttributes(
          propulsion_fields,
          dogma_attributes
        );
        ship_object.attributes.propulsion = propulsion_attributes;

        const fitting_fields = [
          ["cpuOutput", "tf"],
          ["powerOutput", "MW"],
          ["upgradeCapacity", "points"],
          ["turretSlotsLeft", ""],
          ["launcherSlotsLeft", ""],
          ["hiSlots", ""],
          ["medSlots", ""],
          ["lowSlots", ""],
          ["upgradeSlotsLeft", ""]
        ];
        const fitting_attributes = gatherAttributes(
          fitting_fields,
          dogma_attributes
        );
        ship_object.fitting = fitting_attributes;

        const requirement_fields = [
          "requiredSkill1",
          "requiredSkill2",
          "requiredSkill3",
          "requiredSkill4",
          "requiredSkill5"
        ];
        const requirement_attributes = dogma_attributes
          .filter(x => requirement_fields.includes(x.name))
          .sort(
            (a, b) =>
              requirement_fields.indexOf(a.name) -
              requirement_fields.indexOf(b.name)
          )
          .map(x => getRequirements(x, ship.dogma_attributes, this.skills))
          .flat();
        ship_object.requirements = requirement_attributes;

        this.ship = ship_object;
        this.loading = false;
      });
    }
  },
  created() {
    this.loadShipDetails();
  }
};
</script>

<style lang="scss" scoped>
.indent {
  $sizes: 1, 2, 3, 4, 5, 6;
  @each $size in $sizes {
    &--#{$size} {
      padding-left: 2rem * $size;
    }
  }
}
</style>
